import { lazy, Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'

import { CrudeProvider } from './context/CrudeProvider'
import { ProductsProvider } from './context/ProductsProvider'
import { PetroChemicalProvider } from './context/PetroChemicalProvider'
import { CharteringProvider } from './context/CharteringProvider'
import { DerivativesProvider } from './context/DerivativesProvider'

import Layout from './components/Layout'
import Loading from './components/Loading'
import RequireAuth from './components/RequireAuth'
import NotFoundPage from './pages/NotFoundPage'
import SplashPage from './pages/SplashPage'
import LoginPage from './pages/LoginPage'
import LoginPageNoneAd from './pages/LoginPageNotAD'
import RegisterPage from './pages/RegisterPage'
import { SearchProvider } from './context/SearchProvider'
import InAppPage from './pages/InAppPage'
import WebViewPage from './pages/WebViewPage'

const HomePage = lazy(() => import('./pages/HomePage'))
const CrudePage = lazy(() => import('./pages/CrudePage'))
const CrudeWebInputPage = lazy(() => import('./pages/CrudeWebInputPage'))
const ProductsPage = lazy(() => import('./pages/ProductsPage'))
const ProductsWebInputPage = lazy(() => import('./pages/ProductsWebInputPage'))
const PetrochemicalPage = lazy(() => import('./pages/PetrochemicalPage'))
const PetrochemicalWebInputPage = lazy(() => import('./pages/PetrochemicalWebInputPage'))
const CharteringPage = lazy(() => import('./pages/CharteringPage'))
const CharteringWebInputPage = lazy(() => import('./pages/CharteringWebInputPage'))
const DerivativesPage = lazy(() => import('./pages/DerivativesPage'))
const DerivativesWebInputPage = lazy(() => import('./pages/DerivativesWebInputPage'))
const MarketAnalysisPage = lazy(() => import('./pages/MarketAnalysisPage'))
const NewsPage = lazy(() => import('./pages/NewsPage'))
const NewsDetailsPage = lazy(() => import('./pages/NewsDetailsPage'))
const NewsCreatePage = lazy(() => import('./pages/NewsCreatePage'))
const ProfilePage = lazy(() => import('./pages/ProfilePage'))
const SearchResultPage = lazy(() => import('./pages/SearchResultPage'))

const App = () => {
	return (
		<Routes>
			<Route path="/" element={<SplashPage />} />
			<Route path="/login" element={<LoginPage />} />
			<Route path="/login-none-ad" element={<LoginPageNoneAd />} />
			<Route path="/register" element={<RegisterPage />} />
			<Route path="/mobile-app" element={<InAppPage />} />
			<Route path="/web-view" element={<WebViewPage />} />

			<Route element={<Layout />}>
				<Route element={<RequireAuth />}>
					<Route
						path="home"
						element={
							<Suspense fallback={<Loading />}>
								<HomePage />
							</Suspense>
						}
					/>
					<Route
						path="crude"
						element={
							<Suspense fallback={<Loading />}>
								<CrudePage />
							</Suspense>
						}
					/>
					<Route
						path="crude/form"
						element={
							<Suspense fallback={<Loading />}>
								<CrudeProvider>
									<CrudeWebInputPage />
								</CrudeProvider>
							</Suspense>
						}
					/>
					<Route
						path="crude/:ws"
						element={
							<Suspense fallback={<Loading />}>
								<NewsPage />
							</Suspense>
						}
					/>

					<Route
						path="products"
						element={
							<Suspense fallback={<Loading />}>
								<ProductsPage />
							</Suspense>
						}
					/>
					<Route
						path="products/form"
						element={
							<Suspense fallback={<Loading />}>
								<ProductsProvider>
									<ProductsWebInputPage />
								</ProductsProvider>
							</Suspense>
						}
					/>
					<Route
						path="products/:ws"
						element={
							<Suspense fallback={<Loading />}>
								<NewsPage />
							</Suspense>
						}
					/>

					<Route
						path="petrochemical"
						element={
							<Suspense fallback={<Loading />}>
								<PetrochemicalPage />
							</Suspense>
						}
					/>
					<Route
						path="petrochemical/form"
						element={
							<Suspense fallback={<Loading />}>
								<PetroChemicalProvider>
									<PetrochemicalWebInputPage />
								</PetroChemicalProvider>
							</Suspense>
						}
					/>
					<Route
						path="petrochemical/:ws"
						element={
							<Suspense fallback={<Loading />}>
								<NewsPage />
							</Suspense>
						}
					/>

					<Route
						path="chartering"
						element={
							<Suspense fallback={<Loading />}>
								<CharteringPage />
							</Suspense>
						}
					/>
					<Route
						path="chartering/form"
						element={
							<Suspense fallback={<Loading />}>
								<CharteringProvider>
									<CharteringWebInputPage />
								</CharteringProvider>
							</Suspense>
						}
					/>
					<Route
						path="chartering/:ws"
						element={
							<Suspense fallback={<Loading />}>
								<NewsPage />
							</Suspense>
						}
					/>

					<Route
						path="derivatives"
						element={
							<Suspense fallback={<Loading />}>
								<DerivativesPage />
							</Suspense>
						}
					/>
					<Route
						path="derivatives/form"
						element={
							<Suspense fallback={<Loading />}>
								<DerivativesProvider>
									<DerivativesWebInputPage />
								</DerivativesProvider>
							</Suspense>
						}
					/>
					<Route
						path="derivatives/:ws"
						element={
							<Suspense fallback={<Loading />}>
								<NewsPage />
							</Suspense>
						}
					/>

					<Route
						path="market-analysis"
						element={
							<Suspense fallback={<Loading />}>
								<MarketAnalysisPage />
							</Suspense>
						}
					/>
					<Route
						path="market-analysis/:ws"
						element={
							<Suspense fallback={<Loading />}>
								<NewsPage />
							</Suspense>
						}
					/>

					<Route
						path="news"
						element={
							<Suspense fallback={<Loading />}>
								<NewsPage />
							</Suspense>
						}
					/>
					<Route
						path="news/:ws/:newsID"
						element={
							<Suspense fallback={<Loading />}>
								<NewsDetailsPage />
							</Suspense>
						}
					/>
					<Route
						path="news/create"
						element={
							<Suspense fallback={<Loading />}>
								<NewsCreatePage />
							</Suspense>
						}
					/>

					<Route
						path="profile"
						element={
							<Suspense fallback={<Loading />}>
								<ProfilePage />
							</Suspense>
						}
					/>

					<Route
						path="search-results"
						element={
							<Suspense fallback={<Loading />}>
								<SearchProvider>
									<SearchResultPage />
								</SearchProvider>
							</Suspense>
						}
					/>
				</Route>
			</Route>

			<Route path="*" element={<NotFoundPage />} />
		</Routes>
	)
}

export default App
